import { Link } from "react-router-dom";
import { Sun, Moon } from "lucide-react";
import { APP_NAME } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/hooks/useTheme";
import logo from "@/assets/business-pal-logo.png";

export function FooterSection() {
  const { isDark, toggleTheme } = useTheme();

  return (
    <footer className="border-t bg-card">
      <div className="container px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src={logo} alt={APP_NAME} className="h-8 w-auto object-contain" width="32" height="32" />
              <span className="font-bold">{APP_NAME}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              The scheduling platform that helps service businesses save time and grow.
            </p>
          </div>

          {/* Product */}
          <div>
            <p className="font-semibold mb-4">Product</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/features" className="hover:text-foreground transition-colors">Features</Link></li>
              <li><Link to="/industries" className="hover:text-foreground transition-colors">Industries</Link></li>
              <li><Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link></li>
              <li><Link to="/ai-hr" className="hover:text-foreground transition-colors">AI HR & Recruitment</Link></li>
              <li><Link to="/greeting-cards" className="hover:text-foreground transition-colors">AI Greeting Cards</Link></li>
              <li><Link to="/greeting-cards/gallery" className="hover:text-foreground transition-colors">Free eCard Templates</Link></li>
              <li><Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link></li>
              <li><Link to="/signup/business" className="hover:text-foreground transition-colors">Get Started</Link></li>
              <li><Link to="/help" className="hover:text-foreground transition-colors">Documentation</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className="font-semibold mb-4">Company</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/contact" className="hover:text-foreground transition-colors">Contact Us</Link></li>
              <li><Link to="/status" className="hover:text-foreground transition-colors">System Status</Link></li>
              <li><Link to="/sitemap" className="hover:text-foreground transition-colors">Sitemap</Link></li>
              <li><Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-foreground transition-colors">Cookie Policy</Link></li>
              <li><Link to="/legal-notice" className="hover:text-foreground transition-colors">Legal Notice</Link></li>
              <li><button onClick={() => { localStorage.removeItem("cookie-consent"); window.location.reload(); }} className="hover:text-foreground transition-colors text-left">Manage Cookie Preferences</button></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="font-semibold mb-4">Contact</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <a href="mailto:info@businesspal.tech" className="hover:text-foreground transition-colors">
                  info@businesspal.tech
                </a>
              </li>
              <li className="flex items-center gap-4 flex-wrap pt-2">
                {/* X (Twitter) */}
                <a href="https://x.com/BusinessPalTech" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors inline-flex items-center justify-center min-w-[44px] min-h-[44px]" aria-label="X (Twitter)">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                {/* Facebook */}
                <a href="https://www.facebook.com/profile.php?id=61578798140769" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors inline-flex items-center justify-center min-w-[44px] min-h-[44px]" aria-label="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                  </svg>
                </a>
                {/* Instagram */}
                <a href="https://www.instagram.com/businesspaltech/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors inline-flex items-center justify-center min-w-[44px] min-h-[44px]" aria-label="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                {/* YouTube */}
                <a href="https://www.youtube.com/@BusinessPalTech" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors inline-flex items-center justify-center min-w-[44px] min-h-[44px]" aria-label="YouTube">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                {/* TikTok */}
                <a href="https://www.tiktok.com/@businesspaltech" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors inline-flex items-center justify-center min-w-[44px] min-h-[44px]" aria-label="TikTok">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.28a8.28 8.28 0 0 0 4.76 1.5v-3.4a4.85 4.85 0 0 1-1-.69z"/>
                  </svg>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} {APP_NAME}. Made with ❤️ in Ireland.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleTheme}
            className="gap-2"
          >
            {isDark ? (
              <>
                <Sun className="h-4 w-4" />
                Light Mode
              </>
            ) : (
              <>
                <Moon className="h-4 w-4" />
                Dark Mode
              </>
            )}
          </Button>
        </div>
      </div>
    </footer>
  );
}